import api from './api';

export interface Milestone {
  milestoneID: number;
  projectID: number;
  name: string;
  beschreibung?: string;
  zielDatum?: string;
  status: number; // 0=Offen, 1=InArbeit, 2=Erledigt, 3=Abgebrochen
  fortschritt: number;
  erstelltAm: string;
  geaendertAm: string;
  erledigtAm?: string;
  ticketCount?: number;
  completedTicketCount?: number;
}

export interface CreateMilestoneDto {
  projectID: number;
  name: string;
  beschreibung?: string;
  zielDatum?: string;
}

export interface UpdateMilestoneDto {
  name?: string;
  beschreibung?: string;
  zielDatum?: string;
  status?: number;
  fortschritt?: number;
}

export interface MilestoneTicket {
  ticketID: number;
  titel: string;
  was: string;
  wie?: string;
  wann?: string;
  status: string;
  prioritaet?: string;
  erstelltAm: string;
}

export const milestoneService = {
  // Get all milestones
  async getAll(): Promise<Milestone[]> {
    try {
      const response = await api.get<Milestone[]>('/milestones');
      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden aller Meilensteine:', error);
      return [];
    }
  },

  // Get single milestone
  async getById(id: number): Promise<Milestone | null> {
    try {
      const response = await api.get<Milestone>(`/milestones/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Laden des Meilensteins ${id}:`, error);
      return null;
    }
  },

  // Get all milestones for a project
  async getByProject(projectId: number): Promise<Milestone[]> {
    try {
      const response = await api.get<Milestone[]>(`/milestones/project/${projectId}`);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Laden der Meilensteine für Projekt ${projectId}:`, error);
      return [];
    }
  },

  // Create new milestone
  async create(data: CreateMilestoneDto): Promise<Milestone | null> {
    try {
      const response = await api.post<Milestone>('/milestones', data);
      return response.data;
    } catch (error) {
      console.error('Fehler beim Erstellen des Meilensteins:', error);
      return null;
    }
  },

  // Update milestone
  async update(id: number, data: UpdateMilestoneDto): Promise<Milestone | null> {
    try {
      const response = await api.put<Milestone>(`/milestones/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Aktualisieren des Meilensteins ${id}:`, error);
      return null;
    }
  },

  // Delete milestone
  async delete(id: number): Promise<boolean> {
    try {
      await api.delete(`/milestones/${id}`);
      return true;
    } catch (error) {
      console.error(`Fehler beim Löschen des Meilensteins ${id}:`, error);
      return false;
    }
  },

  // Get tickets for a milestone
  async getTickets(id: number): Promise<MilestoneTicket[]> {
    try {
      const response = await api.get<MilestoneTicket[]>(`/milestones/${id}/tickets`);
      return response.data;
    } catch (error) {
      console.error(`Fehler beim Laden der Tickets für Meilenstein ${id}:`, error);
      return [];
    }
  },

  // Assign ticket to milestone
  async assignTicket(milestoneId: number, ticketId: number): Promise<boolean> {
    try {
      await api.post(`/milestones/${milestoneId}/tickets/${ticketId}`);
      return true;
    } catch (error) {
      console.error(`Fehler beim Zuordnen von Ticket ${ticketId} zu Meilenstein ${milestoneId}:`, error);
      return false;
    }
  },

  // Remove ticket from milestone
  async removeTicket(milestoneId: number, ticketId: number): Promise<boolean> {
    try {
      await api.delete(`/milestones/${milestoneId}/tickets/${ticketId}`);
      return true;
    } catch (error) {
      console.error(`Fehler beim Entfernen von Ticket ${ticketId} von Meilenstein ${milestoneId}:`, error);
      return false;
    }
  },
};
